import { useErrorShakeAnimation } from "Payments/AddPaymentMethod/CreditCardEntry/styles";
import { AnimatePresence, motion } from "framer-motion";

import FnrInput2 from "UI/FnrInput2/FnrInput2";
import { FnrCombobox } from "UI/FnrSelectList3";

import ExtendedFieldErrorMessage from "./ExtendedFieldErrorMessage";
import getIconName from "./getIconName";
import { Label, RequiredIcon } from "./styles";

export const MAXIMUM_SELECT_LIST_RANGE_LENGTH = 10;
export const YEAR_RANGE_CONTEXT = "year";

/**
 *
 * @param {string} value
 * @param {function} onChange - updates text
 * @param {object} field
 * @param {number} field.ID
 * @param {string} field.icon
 * @param {number} field.maximum
 * @param {number} field.minimum
 * @param {string} field.title
 * @param {string} field.type
 * @param {string} error
 */

export default function NumberField({
  value,
  onChange,
  field,
  error,
  accentColor = "inherit",
  handleEntryValidation,
}) {
  const { icon, maximum, minimum, title, inputIsRequired, rangeContext } =
    field;

  const { animationProps: errorShakeAnimationProps, shake: errorShake } =
    useErrorShakeAnimation();

  const hasMinAndMax = maximum !== null && minimum !== null;
  let rangeLength;
  if (hasMinAndMax) {
    rangeLength = maximum - minimum;
  }

  const handleSelectChange = (selection) => {
    onChange(selection);
  };

  const checkMinMax = () => {
    let selection = parseFloat(value);
    if (minimum || maximum) {
      if (minimum && selection < minimum) selection = minimum;
      if (maximum && selection > maximum) selection = maximum;
      onChange(selection);
    }
    if (handleEntryValidation) handleEntryValidation(false);
  };

  const onInputValueChange = (val) => {
    const newVal = val.replace(/[^0-9]/g, "");

    if (newVal !== val) errorShake();

    onChange(newVal);

    if (handleEntryValidation) handleEntryValidation(false);
    if (newVal === "") return;

    const intVal = Number(newVal);

    if ((minimum && intVal < minimum) || (maximum && intVal > maximum)) {
      errorShake();
      if (handleEntryValidation) {
        let errorText = "Your number must be ";
        if (minimum !== null && maximum !== null)
          errorText += `between ${minimum} and ${maximum}`;
        else if (minimum !== null) errorText += `greater than ${minimum}`;
        else errorText += `less than ${maximum}`;
        errorText += ".";
        handleEntryValidation(errorText);
      }
    } else handleEntryValidation(false);
  };

  return (
    <div>
      <Label>
        {title}
        {inputIsRequired && <RequiredIcon />}
      </Label>

      {hasMinAndMax && rangeLength <= MAXIMUM_SELECT_LIST_RANGE_LENGTH ? (
        <FnrCombobox
          selection={parseFloat(value)}
          onSelect={handleSelectChange}
          options={getRange(minimum, maximum)}
          icon={getIconName(icon)}
          selectionTextAlign="start"
          accentColor={accentColor}
          focusSize="2px"
          style={{
            width: "100%",
            background: "white",
            color: "currentColor",
            fontWeight: 400,
          }}
        />
      ) : (
        <motion.div {...errorShakeAnimationProps}>
          <FnrInput2
            value={value}
            setValue={onInputValueChange}
            icon={getIconName(icon)}
            inputMode={rangeContext === YEAR_RANGE_CONTEXT ? "text" : "numeric"}
            focusColor={accentColor}
            focusSize="2px"
            iconColor={accentColor}
            iconSize="18px"
            onBlur={checkMinMax}
            style={{ fontWeight: 400 }}
          />
        </motion.div>
      )}
      <AnimatePresence>
        {error ? (
          <ExtendedFieldErrorMessage className="fam_reg_error_msg">
            {error}
          </ExtendedFieldErrorMessage>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export function getRange(start, end) {
  // this shouldn't get called with too large of range,
  // but double check so the app doesn't crash.
  if (end - start > MAXIMUM_SELECT_LIST_RANGE_LENGTH) {
    return [];
  }

  const arr = [];
  for (let i = start; i <= end; ++i) {
    arr.push({
      id: i,
      value: `${i}`,
    });
  }
  return arr;
}
